import { Injectable, inject } from '@angular/core';
import RestService from './core-rest.service';
import AngularHelperService from './angular-helper.service';
import Config from '../utils/config.service';
import AngularBroadcastHelperService from './angular-broadcast-helper.service';
import AppLanguageService from './app-language.service';

@Injectable({ providedIn: 'root' })
export default class DictionaryService {
	private restService: RestService = inject(RestService);
	private angularHelperService: AngularHelperService = inject(AngularHelperService);
	private config: Config = inject(Config);
	private angularBroadcastHelperService: AngularBroadcastHelperService = inject(AngularBroadcastHelperService);
	private appLanguageService: AppLanguageService = inject(AppLanguageService);
	
	private dictionaries: any = {};
	private loadingPromises: any = {};
	private currentLocale: any = 'en_GB';
	private readonly defaultLocale: any = 'en_GB';
	
	getDictionary(locale?) {
		var deferred: any = this.angularHelperService.getDeferredPromise();
		var normalizedLocale: any = this.normalizeLocale(locale || this.currentLocale);

		if (this.dictionaries[normalizedLocale]) {
			deferred.resolve(this.dictionaries[normalizedLocale]);
		} else {
			this.loadDictionary(normalizedLocale)
				.then((dictionary) => {
					deferred.resolve(dictionary);
				})
				.catch((error) => {
					deferred.reject();
				});
		}

		return deferred.promise;
	}
	
	loadDictionary(locale?): Promise<any> {
		var normalizedLocale: any = this.normalizeLocale(locale || this.currentLocale || this.defaultLocale);
		if (this.dictionaries[normalizedLocale]) {
			return this.angularHelperService.getPromise().resolve(this.dictionaries[normalizedLocale]);
		}
		if (this.loadingPromises[normalizedLocale]) {
			return this.loadingPromises[normalizedLocale];
		}
		
		this.loadingPromises[normalizedLocale] = this.restService.getDictionary({ locale: this.toDictionaryLocale(normalizedLocale) })
			.then((dictionaryAsText) => {
				var dictionary: any = this.parseKeyValueDictionary(dictionaryAsText || '');
				this.dictionaries[normalizedLocale] = dictionary;
				if (!window['fallbackDictionary']) {
					window['fallbackDictionary'] = dictionary;
				}
				if (normalizedLocale === this.defaultLocale) {
					window['fallbackDictionary'] = dictionary;
				}
				return dictionary;
			})
			.catch((error) => {
				if (normalizedLocale !== this.defaultLocale && this.dictionaries[this.defaultLocale]) {
					return this.dictionaries[this.defaultLocale];
				}
				throw error;
			})
			.finally(() => {
				delete this.loadingPromises[normalizedLocale];
			});
		
		return this.loadingPromises[normalizedLocale];
	}
	
	setLanguage(locale): Promise<any> {
		var normalizedLocale: any = this.normalizeLocale(locale);
		return this.loadDictionary(normalizedLocale)
			.then((dictionary) => {
				this.currentLocale = normalizedLocale;
				window['fallbackDictionary'] = this.dictionaries[this.defaultLocale] || dictionary;
				this.angularBroadcastHelperService.rootBroadcast(
					this.config.getBroadcastConfig().Dictionary.LOADED,
					normalizedLocale,
					dictionary
				);
				return dictionary;
			});
	}
	
	translate(path, locale?) {
		if (!path) return path;
		var normalizedLocale: any = this.normalizeLocale(locale || this.currentLocale);
		var dictionary: any = this.dictionaries[normalizedLocale] || {};
		var fallbackDictionary: any = this.dictionaries[this.defaultLocale] || {};
		return dictionary[path] || fallbackDictionary[path] || path;
	}
	
	getCurrentLocale() {
		return this.currentLocale;
	}
	
	private normalizeLocale(locale) {
		return this.appLanguageService.normalizeLocale(locale || this.defaultLocale);
	}
	
	private toDictionaryLocale(locale) {
		var normalizedLocale: any = this.normalizeLocale(locale);
		return (normalizedLocale || this.defaultLocale).split('_')[0].toLowerCase();
	}
	
	private parseKeyValueDictionary(dictionaryContent: string) {
		var dictionary: any = {};
		var lines: Array<any> = (dictionaryContent || '').split(/\r?\n/);
		
		for (var i = 0; i < lines.length; i++) {
			var rawLine: any = lines[i];
			if (!rawLine) continue;
			
			var line: any = rawLine.trim();
			if (!line || line.indexOf('#') === 0) continue;
			
			var separatorIndex: any = this.findSeparatorIndex(line);
			if (separatorIndex < 0) continue;
			
			var key: any = this.unescapeValue(line.slice(0, separatorIndex).trim());
			var value: any = this.unescapeValue(line.slice(separatorIndex + 1));
			if (!key) continue;
			dictionary[key] = value;
		}
		
		return dictionary;
	}
	
	private findSeparatorIndex(line) {
		for (var i = 0; i < line.length; i++) {
			if (line[i] === '=' && line[i - 1] !== '\\') {
				return i;
			}
		}
		return -1;
	}
	
	private unescapeValue(input: string) {
		if (input === undefined || input === null) return '';
		return input.replace(/\\([nrt=\\])/g, (match, group) => {
			if (group === 'n') return '\n';
			if (group === 'r') return '\r';
			if (group === 't') return '\t';
			return group;
		});
	}
}
